import React from 'react';
import { Link } from 'react-router-dom';
import { Gift, ClipboardList, Building2, Home } from 'lucide-react';
import SEO from '../components/SEO';
import './ServicioSEO.css';

const GrabadoLaser = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Grabado Láser",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Artelaser3D",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Asturias",
        "addressRegion": "Asturias",
        "addressCountry": "ES"
      }
    },
    "areaServed": [
      { "@type": "City", "name": "Oviedo" },
      { "@type": "City", "name": "Gijón" },
      { "@type": "City", "name": "Avilés" },
      { "@type": "Country", "name": "España" }
    ],
    "description": "Servicio profesional de grabado láser en Asturias. Trabajamos con madera, acrílico, cuero, metal y más. Personalización de regalos, señalética y productos corporativos.",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock"
    }
  };

  const materials = [
    {
      name: 'Madera',
      description: 'Pino, roble, nogal, haya y contrachapado. Ideal para decoración, regalos personalizados y señalética rústica.',
      uses: ['Cuadros decorativos', 'Cajas personalizadas', 'Letras corpóreas', 'Señalética']
    },
    {
      name: 'Acrílico y Plexiglas',
      description: 'Precisión cristalina con acabados profesionales. Perfecto para proyectos modernos y elegantes.',
      uses: ['Trofeos', 'Displays', 'Lámparas', 'Elementos decorativos']
    },
    {
      name: 'Cuero y Piel',
      description: 'Personalización de artículos de cuero genuino y sintético con acabados duraderos.',
      uses: ['Carteras', 'Fundas', 'Cinturones', 'Accesorios']
    },
    {
      name: 'Metal Anodizado',
      description: 'Grabado de alta definición en aluminio anodizado para aplicaciones técnicas y profesionales.',
      uses: ['Placas identificativas', 'Etiquetas industriales', 'Regalos corporativos', 'Trofeos']
    }
  ];

  const applications = [
    {
      title: 'Regalos Personalizados',
      description: 'Crea regalos únicos y memorables para cualquier ocasión: bodas, bautizos, cumpleaños, aniversarios...',
      icon: <Gift size={48} />
    },
    {
      title: 'Señalética Profesional',
      description: 'Placas, rótulos y señalización de alta calidad para empresas, comercios y espacios públicos.',
      icon: <ClipboardList size={48} />
    },
    {
      title: 'Productos Corporativos',
      description: 'Artículos promocionales personalizados con tu logo: llaveros, bolígrafos, libretas y más.',
      icon: <Building2 size={48} />
    },
    {
      title: 'Decoración e Interiorismo',
      description: 'Cuadros, lámparas, separadores y elementos decorativos personalizados para hogares y negocios.',
      icon: <Home size={48} />
    }
  ];

  const faqs = [
    {
      question: '¿Qué materiales se pueden grabar con láser?',
      answer: 'Trabajamos con una amplia variedad: madera (pino, roble, nogal), acrílico, cuero, metal anodizado, cristal, corcho, cartón y algunos plásticos. Consulta con nosotros si tienes un material específico.'
    },
    {
      question: '¿Cuál es el tiempo de entrega para grabado láser?',
      answer: 'Depende del volumen y complejidad del proyecto. Para trabajos sencillos, 24-48 horas. Proyectos más complejos pueden requerir 3-5 días hábiles. Siempre confirmamos el plazo antes de comenzar.'
    },
    {
      question: '¿Necesito proporcionar el diseño?',
      answer: 'Puedes traer tu propio diseño en formato vectorial (AI, EPS, SVG) o te ayudamos a crearlo. También trabajamos con imágenes de alta calidad que vectorizamos para el grabado.'
    },
    {
      question: '¿Hay un mínimo de unidades?',
      answer: 'No, realizamos desde una sola pieza hasta pequeñas series. Para volúmenes superiores a 50 unidades, consulta descuentos especiales.'
    }
  ];

  return (
    <>
      <SEO 
        title="Grabado Láser en Asturias"
        description="Servicio profesional de grabado láser en Asturias. Madera, acrílico, cuero, metal. Regalos personalizados, señalética y productos corporativos. Envíos a toda España."
        keywords="grabado láser Asturias, grabado láser Oviedo, grabado láser Gijón, grabado láser Avilés, grabado personalizado, grabado madera, grabado cuero"
        canonicalUrl="https://artelaser3d.es/grabado-laser-asturias"
        schema={schema}
      />

      <section className="page-hero page-hero--laser">
        <div className="page-hero__container">
          <span className="page-hero__badge">Grabado Láser</span>
          <h1 className="page-hero__title">
            Grabado láser de
            <span className="text-primary"> precisión</span> en Asturias
          </h1>
          <p className="page-hero__description">
            Servicio profesional de grabado y corte láser con la máxima calidad. 
            Trabajamos con madera, acrílico, cuero, metal y más. Personalización 
            para particulares y empresas.
          </p>
          <div className="page-hero__cta">
            <Link to="/presupuesto" className="btn btn--white btn--lg">
              Solicitar Presupuesto
            </Link>
          </div>
        </div>
      </section>

      <section className="section section--materials section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Materiales</span>
            <h2>Trabajamos con los mejores materiales</h2>
            <p className="section__description">
              Seleccionamos cuidadosamente cada material para garantizar 
              resultados excepcionales en cada proyecto.
            </p>
          </header>

          <div className="materials-grid">
            {materials.map((material, index) => (
              <article key={index} className="material-card">
                <h3>{material.name}</h3>
                <p>{material.description}</p>
                <div className="material-card__uses">
                  <strong>Aplicaciones:</strong>
                  <ul>
                    {material.uses.map((use, i) => (
                      <li key={i}>{use}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--applications section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">Aplicaciones</span>
            <h2>Soluciones para cada necesidad</h2>
          </header>

          <div className="applications-grid">
            {applications.map((app, index) => (
              <article key={index} className="application-card">
                <span className="application-card__icon">{app.icon}</span>
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--faq section--visible">
        <div className="section__container">
          <header className="section__header section__header--center">
            <span className="section__badge">FAQ</span>
            <h2>Preguntas frecuentes sobre grabado láser</h2>
          </header>

          <div className="faq-list">
            {faqs.map((faq, index) => (
              <article key={index} className="faq-item">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--cta section--cta--laser section--visible">
        <div className="cta__content">
          <h2 className="cta__title--laser">¿Listo para tu proyecto de grabado láser?</h2>
          <p className="cta__description--laser">
            Solicita tu presupuesto sin compromiso. Respuesta en 24 horas.
          </p>
          <div className="cta__buttons">
            <Link to="/presupuesto" className="btn btn--primary btn--lg">
              Solicitar Presupuesto Gratis
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default GrabadoLaser;
